import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import { Button } from "@braintrust/braintrust-ui-components";
import {
  CheckIcon,
  PersonAddIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useSendInvitationMutation } from "@js/apps/jobs/api";
import { Snackbar } from "@js/components/snackbar";
import { isErrorWithDataAndRootMessage } from "@js/types/errors";
import type { TalentInviteSuggestion } from "@js/types/jobs";

const INVITE_GENERIC_ERROR_MESSAGE = "Sorry - this talent can't be invited.";

const getInviteErrorMessage = (error: unknown) => {
  if (!isErrorWithDataAndRootMessage(error)) {
    return INVITE_GENERIC_ERROR_MESSAGE;
  }

  // Server errors (5xx) come with a list of messages
  const message: string | string[] = error.data._error;
  return Array.isArray(message) ? message.join(" ") : message;
};

type InviteToJobButtonProps = {
  freelancer: TalentInviteSuggestion;
  jobId: number;
  disabled?: boolean;
  notificationHandler: () => void;
  onInvite?: () => void;
} & Pick<ButtonProps, "size">;

export const InviteToJobButton = ({
  freelancer,
  jobId,
  disabled,
  notificationHandler,
  onInvite,
  ...buttonProps
}: InviteToJobButtonProps) => {
  const [sendInvitation, { isLoading }] = useSendInvitationMutation();
  const isTalentInvitated = !!freelancer.invite_status;
  const label = isTalentInvitated ? (
    <>
      Invited! <CheckIcon style={{ fontSize: 20, marginLeft: 8 }} />
    </>
  ) : (
    <>
      Invite to job{" "}
      <PersonAddIcon
        style={{
          fontSize: 24,
          marginLeft: 8,
        }}
      />
    </>
  );

  return (
    <Button
      shape="squared"
      disabledType="opacity"
      disabled={disabled}
      variant={isTalentInvitated ? "dark-violet" : "primary"}
      sx={{
        pointerEvents: isTalentInvitated ? "none" : undefined,
        whiteSpace: "nowrap",
      }}
      onClick={() => {
        if (isTalentInvitated || isLoading) {
          return undefined;
        }

        onInvite?.();

        sendInvitation({ jobId, freelancersIds: [freelancer.id] })
          .unwrap()
          .then(() => {
            notificationHandler();
          })
          .catch((error: unknown) => {
            Snackbar.error(getInviteErrorMessage(error));
          });
      }}
      {...buttonProps}
    >
      {label}
    </Button>
  );
};
